import { useMemo } from 'react'

import { ChevronDown } from 'lucide-react'
import { Link, NavLink, useLocation } from 'react-router'

import { cn } from '@fortem-monorepo/js-utilities'
import {
  type AppLink,
  type AppPopover,
  useActions,
  useUserContext,
} from '@fortem-monorepo/react-spa'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
  TabsBase,
  TabsListBase,
  TabsListProps,
  TabsProps,
  TabsTriggerBase,
  TabsTriggerProps,
} from '@fortem-monorepo/react-ui'

const navigationTabClassnames = [
  'inline-block transition-colors text-[15px]',
  'pb-3',
  'border-b-3 border-b-transparent',
  '[&[data-state="active"]]:text-fortem-orange [&[data-state="active"]]:border-b-fortem-orange',
  '[&:not([data-state="active"])]:hover:text-fortem-orange/60',
]

export interface NavigationTabTriggerProps extends TabsTriggerProps {
  value: string
  activeIfIncludes?: boolean
}

export const NavigationTabTrigger: React.FC<NavigationTabTriggerProps> = ({
  value,
  children,
  className,
  activeIfIncludes = false,
  ...props
}) => {
  const { pathname } = useLocation()
  const isActiveByInheritance = pathname.includes(value)

  return (
    <TabsTriggerBase
      {...(activeIfIncludes && isActiveByInheritance && { 'data-state': 'active' })}
      asChild
      value={value}
      className={cn(navigationTabClassnames, className)}
      {...props}
    >
      <Link to={value}>{children}</Link>
    </TabsTriggerBase>
  )
}

export interface NavigationTabsProps extends Omit<TabsProps, 'value'> {}

export const NavigationTabs: React.FC<NavigationTabsProps> = ({ children, ...props }) => {
  const { pathname } = useLocation()
  return (
    <TabsBase value={pathname} {...props}>
      {children}
    </TabsBase>
  )
}

export interface NavigationTabsListProps extends TabsListProps {
  links?: (AppLink<any, any> | AppPopover<any, any, any>)[]
}

export const NavigationTabsList: React.FC<NavigationTabsListProps> = ({
  links,
  children,
  className,
  ...props
}) => {
  const { user } = useUserContext()
  const { filterActions } = useActions()
  const { pathname } = useLocation()

  const filteredLinks = useMemo(() => {
    return filterActions(links ?? [])
  }, [links, user, filterActions])

  return (
    <TabsListBase className={cn('flex gap-4 w-fit', className)} {...props}>
      {filteredLinks.length > 0 &&
        filteredLinks.map((item) => {
          if (item.type === 'link') {
            return (
              <NavigationTabTrigger value={item.link} key={item.label}>
                {item.label}
              </NavigationTabTrigger>
            )
          }

          if (item.type === 'popover') {
            const childIsActive = item.children
              .filter((child) => child.type === 'link')
              .some((child) => pathname.includes(child.link))

            return (
              <DropdownMenu key={item.label}>
                <DropdownMenuTrigger
                  {...(childIsActive && { 'data-state': 'active' })}
                  className={cn(navigationTabClassnames, 'cursor-pointer flex items-center gap-1')}
                >
                  {item.label}
                  <ChevronDown size={20} />
                </DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuGroup>
                    {item.children.map((child) => {
                      if (child.type === 'link') {
                        const isActive = pathname.includes(child.link)
                        return (
                          <DropdownMenuItem asChild key={child.label}>
                            <NavLink
                              to={child.link}
                              className={cn({
                                'pointer-events-none font-medium opacity-50': isActive,
                              })}
                            >
                              {child.label}
                            </NavLink>
                          </DropdownMenuItem>
                        )
                      }
                    })}
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )
          }
        })}
      {children}
    </TabsListBase>
  )
}
